/** Strict, shared boundary for quote renders in both live streams and history.
 * No amount arithmetic, inferred target, authorization or write intent here.
 */
import type { QuoteBasis } from "./factorQuotes";

export type QuoteValidity = {
  as_of_date: string;
  prices: {
    path: string;
    label: string;
    source: string | null;
    confidence: string | null;
    state: string | null;
    valid_until: string | null;
    status: "current" | "expired" | "unknown";
  }[];
  unconfirmed: {
    path: string;
    label: string;
    source: string | null;
    confidence: string | null;
    state: string | null;
  }[];
};
type Origin = { source_call_id?: string };
export type QuoteSimulationArtifact = Origin & {
  kind: "quote_simulation";
  quote_id: string;
  base_revision: number;
  scope: "quote" | "line_quote";
  before: {
    qty: number;
    unit_price: number | null;
    order_price?: number | null;
    line_order_price?: number | null;
  };
  after: QuoteSimulationArtifact["before"];
  exact: boolean;
  caveats: string[];
  validity?: QuoteValidity;
};
export type QuoteProposalKind =
  | "create"
  | "factor_edit"
  | "reselect"
  | "create_project"
  | "new_version"
  | "clone"
  | "answer_question";
export type QuoteProposalArtifact = Origin & {
  kind: "quote_proposal";
  proposal_id: string;
  proposal_kind: QuoteProposalKind;
  quote_id: string | null;
  quote_no: string | null;
  base_revision: number | null;
  items: { path: string; label: string; before?: unknown; after?: unknown }[];
  before: unknown;
  after: unknown;
  quota: { charge: number } | null;
  notice: string | null;
  expires_at: string;
};
export type QuoteCandidate = {
  quote_id: string;
  quote_no: string;
  title: string | null;
  customer: string | null;
  drawing_match: string | null;
  qty: number;
  status: string;
  created_at: string | null;
  in_project: boolean;
  customer_quote_no?: string;
  superseded_by_version?: number;
  superseded_by?: { quote_id: string; version_no: number };
};
export type QuoteCandidatesArtifact = Origin & {
  kind: "quote_candidates";
  purpose: "find";
  items: QuoteCandidate[];
};
export type QuoteProjectArtifact = Origin & {
  kind: "quote_project";
  project_id: string;
  name: string;
  sets: number;
  lines: number;
  made_lines: number;
  need_review: number;
  quota_on_start: number;
  path: string;
  notice: string;
  existing: boolean;
};
export type QuoteExplanationArtifact = Origin & {
  kind: "quote_explanation";
  quote_id: string;
  base_revision: number;
  basis: QuoteBasis;
  anchors: { path: string; label: string }[];
};
export type QuoteArtifactIssue = Origin & {
  kind: "quote_artifact_issue";
  original_kind: string;
  reason: string;
};
export type QuoteChatArtifact =
  | QuoteSimulationArtifact
  | QuoteProposalArtifact
  | QuoteCandidatesArtifact
  | QuoteProjectArtifact
  | QuoteExplanationArtifact
  | QuoteArtifactIssue;

export const quoteChatKinds = [
  "quote_simulation",
  "quote_proposal",
  "quote_candidates",
  "quote_project",
  "quote_explanation",
  "quote_artifact_issue",
] as const;
export function isQuoteChatArtifact(value: {
  kind: string;
}): value is QuoteChatArtifact {
  return (quoteChatKinds as readonly string[]).includes(value.kind);
}
export const quoteUuid = (v: unknown): v is string =>
  typeof v === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
const record = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === "object" && !Array.isArray(v);
const string = (v: unknown): v is string => typeof v === "string";
const nullableString = (v: unknown) => v === null || string(v);
const integer = (v: unknown): v is number =>
  typeof v === "number" && Number.isSafeInteger(v) && v >= 0;
const finite = (v: unknown) =>
  v === null || (typeof v === "number" && Number.isFinite(v));
const arrayOf = (
  v: unknown,
  predicate: (entry: unknown) => boolean,
): v is unknown[] => Array.isArray(v) && v.every(predicate);
const ref = (v: unknown) => record(v) && string(v.id) && string(v.label);
const namedPath = (v: unknown) =>
  record(v) && string(v.path) && !!v.path && string(v.label);
const source = (v: Record<string, unknown>) =>
  [v.source, v.confidence, v.state].every(nullableString);
const optional = (v: unknown, predicate: (entry: unknown) => boolean) =>
  v === undefined || predicate(v);

function validBasis(v: unknown): boolean {
  if (!record(v) || !["ok", "skipped", "error"].includes(String(v.status)))
    return false;
  if (
    !optional(v.lines, (x) =>
      arrayOf(
        x,
        (l) =>
          record(l) &&
          ref(l) &&
          finite(l.amount) &&
          nullableString(l.currency) &&
          nullableString(l.formula) &&
          arrayOf(l.depends_on, ref),
      ),
    )
  )
    return false;
  if (
    !optional(v.requirements, (x) =>
      arrayOf(
        x,
        (r) =>
          record(r) &&
          ref(r) &&
          typeof r.critical === "boolean" &&
          nullableString(r.description) &&
          nullableString(r.state) &&
          nullableString(r.state_label) &&
          arrayOf(r.covered_by, ref),
      ),
    )
  )
    return false;
  if (
    !optional(v.open_claims, (x) =>
      arrayOf(
        x,
        (c) =>
          record(c) &&
          ref(c) &&
          string(c.text) &&
          string(c.state) &&
          string(c.state_label) &&
          typeof c.critical === "boolean",
      ),
    )
  )
    return false;
  if (
    !optional(v.prices, (x) =>
      arrayOf(
        x,
        (p) =>
          record(p) &&
          ref(p) &&
          finite(p.rate) &&
          [p.currency, p.unit, p.valid_from, p.valid_until, p.source].every(
            nullableString,
          ),
      ),
    )
  )
    return false;
  if (v.quote !== undefined) {
    if (
      !record(v.quote) ||
      ![v.quote.label, v.quote.currency, v.quote.status].every(
        nullableString,
      ) ||
      ![
        v.quote.quantity,
        v.quote.unit_price,
        v.quote.unit_cost,
        v.quote.pricing_ratio,
      ].every(finite) ||
      !optional(v.quote.missing_required, (x) => arrayOf(x, string))
    )
      return false;
  }
  if (v.versions !== undefined) {
    if (
      !record(v.versions) ||
      ![v.versions.engine, v.versions.completeness].every(nullableString) ||
      !arrayOf(
        v.versions.calibrations,
        (c) =>
          record(c) &&
          string(c.id) &&
          nullableString(c.label) &&
          nullableString(c.parameter_version),
      )
    )
      return false;
    const a = v.versions.approval;
    if (
      a !== null &&
      (!record(a) ||
        ![a.by, a.at, a.snapshot_hash].every(nullableString) ||
        !optional(a.effective, (x) => x === null || typeof x === "boolean"))
    )
      return false;
  }
  return true;
}
function validSimulation(v: Record<string, unknown>): boolean {
  if (
    !quoteUuid(v.quote_id) ||
    !integer(v.base_revision) ||
    !["quote", "line_quote"].includes(String(v.scope)) ||
    typeof v.exact !== "boolean" ||
    !arrayOf(v.caveats, string)
  )
    return false;
  const amount = v.scope === "quote" ? "order_price" : "line_order_price";
  for (const side of [v.before, v.after]) {
    if (
      !record(side) ||
      !integer(side.qty) ||
      side.qty < 1 ||
      !finite(side.unit_price) ||
      !finite(side[amount])
    )
      return false;
  }
  if (v.validity !== undefined) {
    const m = v.validity;
    if (
      !record(m) ||
      !string(m.as_of_date) ||
      !arrayOf(
        m.prices,
        (p) =>
          record(p) &&
          namedPath(p) &&
          source(p) &&
          nullableString(p.valid_until) &&
          ["current", "expired", "unknown"].includes(String(p.status)),
      ) ||
      !arrayOf(m.unconfirmed, (p) => record(p) && namedPath(p) && source(p))
    )
      return false;
  }
  return true;
}
function validProposal(v: Record<string, unknown>): boolean {
  return (
    quoteUuid(v.proposal_id) &&
    [
      "create",
      "factor_edit",
      "reselect",
      "create_project",
      "new_version",
      "clone",
      "answer_question",
    ].includes(String(v.proposal_kind)) &&
    (v.quote_id === null || quoteUuid(v.quote_id)) &&
    nullableString(v.quote_no) &&
    (v.base_revision === null || integer(v.base_revision)) &&
    arrayOf(v.items, namedPath) &&
    (v.quota === null || (record(v.quota) && integer(v.quota.charge))) &&
    nullableString(v.notice) &&
    string(v.expires_at) &&
    Number.isFinite(Date.parse(v.expires_at))
  );
}
function validCandidate(v: unknown): boolean {
  if (
    !record(v) ||
    !quoteUuid(v.quote_id) ||
    !string(v.quote_no) ||
    ![v.title, v.customer, v.drawing_match, v.created_at].every(
      nullableString,
    ) ||
    !integer(v.qty) ||
    !string(v.status) ||
    typeof v.in_project !== "boolean"
  )
    return false;
  return (
    optional(v.customer_quote_no, string) &&
    optional(v.superseded_by_version, integer) &&
    optional(
      v.superseded_by,
      (s) => record(s) && quoteUuid(s.quote_id) && integer(s.version_no),
    )
  );
}

/** undefined means non-quote and lets the explicit legacy decoder proceed. */
export function decodeQuoteChatArtifact(
  render: unknown,
  callId?: string,
): QuoteChatArtifact | undefined {
  if (
    !record(render) ||
    !string(render.kind) ||
    render.kind === "quote_worksheet" ||
    !render.kind.startsWith("quote_")
  )
    return undefined;
  let valid = false;
  switch (render.kind) {
    case "quote_simulation":
      valid = validSimulation(render);
      break;
    case "quote_proposal":
      valid = validProposal(render);
      break;
    case "quote_candidates":
      valid =
        render.purpose === "find" &&
        Array.isArray(render.items) &&
        render.items.length <= 5 &&
        render.items.every(validCandidate);
      break;
    case "quote_project":
      valid =
        quoteUuid(render.project_id) &&
        render.path === `/app/quickquote/projects/${render.project_id}` &&
        string(render.name) &&
        integer(render.sets) &&
        render.sets > 0 &&
        [
          render.lines,
          render.made_lines,
          render.need_review,
          render.quota_on_start,
        ].every(integer) &&
        string(render.notice) &&
        typeof render.existing === "boolean";
      break;
    case "quote_explanation":
      valid =
        quoteUuid(render.quote_id) &&
        integer(render.base_revision) &&
        validBasis(render.basis) &&
        arrayOf(render.anchors, namedPath);
      break;
  }
  if (!valid)
    return {
      kind: "quote_artifact_issue",
      original_kind: render.kind,
      reason: "报价卡结构无效或暂不支持，请重新读取原报价",
      source_call_id: callId,
    };
  return { ...render, source_call_id: callId } as QuoteChatArtifact;
}

export function quoteChatArtifactKey(a: QuoteChatArtifact): string {
  switch (a.kind) {
    case "quote_proposal":
      return `quote_proposal:${a.proposal_id}`;
    case "quote_project":
      return `quote_project:${a.project_id}`;
    case "quote_candidates":
      return `quote_candidates:${a.source_call_id ?? ""}`;
    case "quote_simulation":
    case "quote_explanation":
      return `${a.kind}:${a.source_call_id ?? ""}:${a.quote_id}:${a.base_revision}`;
    case "quote_artifact_issue":
      return `quote_artifact_issue:${a.source_call_id ?? ""}:${a.original_kind}`;
  }
}
